<script setup>
/* eslint-disable */
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useGameStore } from '@/store/game'
import { RaceSimulator, RACE_REWARDS } from '@/raceLogic'
import { getAvailableCircuits, RACE_CIRCUITS } from '../constants/raceCircuits'
import { getStatGrade, GRADE_VALUES } from '@/utils/chocoboStats'
import { BackgroundRaceSimulator } from '@/utils/backgroundRaceSim'
import { JockeyFactory } from '@/services/jockeyFactory'
import { ChocoboFactory } from '@/services/chocoboFactory'
import RaceVisualization from './RaceVisualization.vue'
import RaceLoadingOverlay from './RaceLoadingOverlay.vue'
import { getChocoboColorClass, getStatGradeClass } from '@/utils/colorUtils'
import { applyThemeClasses } from '@/utils/themeColors'

const REGIONAL_CIRCUIT_ID = 'regional'

const props = defineProps({
  selectedChocobo: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'raceComplete'])
const gameStore = useGameStore()
const selectedJockey = ref(null)
const selectedRaceLength = ref('medium')
const selectedCircuit = ref(null)
const showRaceVisualization = ref(false)
const raceResults = ref(null)
const preloadedImages = ref([])
const racingChocobos = ref([])
const finalizedOpponents = ref(null)
const availableJockeys = ref([]);



const RACE_LENGTHS = {
  SHORT: {
    id: 'short',
    name: 'Short Race',
    description: 'Quick sprint - 800m',
    stamina_factor: 0.5
  },
  MEDIUM: {
    id: 'medium',
    name: 'Medium Race',
    description: 'Standard race - 2000m',
    stamina_factor: 1
  },
  LONG: {
    id: 'long',
    name: 'Long Race',
    description: 'Endurance race - 5000m',
    stamina_factor: 2
  }
}

const CIRCUIT_JOCKEY_REQUIREMENTS = {
  regional: { minGrade: 'F', preferredGrades: ['D', 'C', 'B'] },
  continental: { minGrade: 'D', preferredGrades: ['C', 'B', 'A'] },
  transcontinental: { minGrade: 'C', preferredGrades: ['B', 'A', 'S'] },
  global: { minGrade: 'B', preferredGrades: ['A', 'S'] },
  saucer: { minGrade: 'A', preferredGrades: ['S'] }
};

const getGradeValue = (grade) => {
  const values = { 'S': 6, 'A': 5, 'B': 4, 'C': 3, 'D': 2, 'E': 1, 'F': 0 };
  return values[grade] || 0;
};


const cachedOpponents = ref(new Map());
const cachedJockeys = ref(new Map());

watch(() => selectedCircuit.value, (newCircuit) => {
  if (newCircuit) {
    // Check if we already have opponents for this circuit
    if (cachedOpponents.value.has(newCircuit.id)) {
      finalizedOpponents.value = cachedOpponents.value.get(newCircuit.id);
    } else {
      const eligibleRegions = newCircuit.eligibleRegions;
      const targets = {
        regional: { minGrade: 'D', maxGrade: 'B-', count: 5 },
        continental: { minGrade: 'C+', maxGrade: 'A-', count: 5 },
        transcontinental: { minGrade: 'B+', maxGrade: 'S-', count: 5 },
        global: { minGrade: 'A-', maxGrade: 'S+', count: 5 },
        saucer: { minGrade: 'A+', maxGrade: 'S+', count: 5 }
      };

      const target = targets[newCircuit.id];

      // Find existing chocobos that meet circuit requirements
      const existingOpponents = gameStore.chocobos
        .filter(chocobo => 
          chocobo.id !== props.selectedChocobo.id &&
          !gameStore.playerOwner.chocoboIds.includes(chocobo.id) &&
          chocobo.level >= newCircuit.unlockLevel &&
          eligibleRegions.includes(chocobo.region)
        );

      // If not enough opponents, generate new ones
      if (existingOpponents.length < 5) {
        const neededCount = 5 - existingOpponents.length;
        const newOpponents = [];

        for (let i = 0; i < neededCount; i++) {
          const chocobo = ChocoboFactory.generateHighLevelChocobo(
            newCircuit.unlockLevel,
            newCircuit.unlockLevel + 10,
            gameStore.currentDay
          );

          chocobo.region = eligibleRegions[Math.floor(Math.random() * eligibleRegions.length)];

          const grades = Object.keys(GRADE_VALUES).filter(grade => 
            GRADE_VALUES[grade] >= GRADE_VALUES[target.minGrade] &&
            GRADE_VALUES[grade] <= GRADE_VALUES[target.maxGrade]
          );
          
          const targetGrade = grades[Math.floor(Math.random() * grades.length)];
          const targetValue = calculateTargetStatForGrade(targetGrade);

          chocobo.baseSpeed = Math.floor(targetValue * 0.7);
          chocobo.baseStamina = Math.floor(targetValue * 0.7);
          chocobo.speedBonus = Math.floor(targetValue * 0.3);
          chocobo.staminaBonus = Math.floor(targetValue * 0.3);
          chocobo.updateTotalStats();

          newOpponents.push(chocobo);
          gameStore.chocobos.push(chocobo);

          const eligibleOwners = gameStore.owners.filter(owner =>
            !owner.isPlayer &&
            owner.money >= newCircuit.entryFee &&
            owner.chocoboIds.length < 15 &&
            eligibleRegions.includes(owner.homeRegion)
          );

          if (eligibleOwners.length > 0) {
            const randomOwner = eligibleOwners[Math.floor(Math.random() * eligibleOwners.length)];
            randomOwner.chocoboIds.push(chocobo.id);
          }
        }

        finalizedOpponents.value = [...existingOpponents, ...newOpponents];
      } else {
        finalizedOpponents.value = existingOpponents
          .sort(() => Math.random() - 0.5)
          .slice(0, 5);
      }

      // Cache the opponents for this circuit
      cachedOpponents.value.set(newCircuit.id, finalizedOpponents.value);
    }

    // Check if we already have jockeys for this circuit
    if (cachedJockeys.value.has(newCircuit.id)) {
      availableJockeys.value = cachedJockeys.value.get(newCircuit.id);
    } else {
      const raceRegion = newCircuit.eligibleRegions[0];
      const playerChocobo = props.selectedChocobo;
      const requirements = CIRCUIT_JOCKEY_REQUIREMENTS[newCircuit.id];
      
      // Filter jockeys based on circuit requirements
      const freeJockeys = gameStore.jockeys.filter(jockey => {
        const gradeValue = getGradeValue(jockey.skillGrade);
        const minGradeValue = getGradeValue(requirements.minGrade);
        return (
          jockey.recentForm[jockey.recentForm.length - 1] === null &&
          gradeValue >= minGradeValue
        );
      });

      const regionalJockeys = JockeyFactory.filterJockeysByRegion(freeJockeys, raceRegion);
      const selectedJockeys = [];
      const usedNames = new Set();

      // First priority: Get jockeys with preferred grades for this circuit
      const preferredJockeys = regionalJockeys.filter(jockey => 
        requirements.preferredGrades.includes(jockey.skillGrade)
      ).sort(() => Math.random() - 0.5);

      // Add successful pairs but only if they meet minimum grade requirement
      const successfulPairs = regionalJockeys.filter(jockey => {
        const raceHistory = playerChocobo.raceHistory.filter(race =>
          race.jockeyId === jockey.id && race.position <= 3
        );
        const gradeValue = getGradeValue(jockey.skillGrade);
        const minGradeValue = getGradeValue(requirements.minGrade);
        return raceHistory.length > 0 && 
               !usedNames.has(jockey.name) && 
               gradeValue >= minGradeValue;
      });

      // Add successful pairs first
      successfulPairs.forEach(jockey => {
        if (selectedJockeys.length < 6) {
          selectedJockeys.push(jockey);
          usedNames.add(jockey.name);
        }
      });

      // Then add preferred grade jockeys
      preferredJockeys.forEach(jockey => {
        if (selectedJockeys.length < 6 && !usedNames.has(jockey.name)) {
          selectedJockeys.push(jockey);
          usedNames.add(jockey.name);
        }
      });

      // If we still need more jockeys, generate new ones with appropriate grades
      while (selectedJockeys.length < 6) {
        const newJockey = JockeyFactory.generateRandomJockey(raceRegion);
        
        // For top circuits, ensure high skill levels
        if (['global', 'saucer'].includes(newCircuit.id)) {
          newJockey.skill = Math.floor(Math.random() * 15) + 85; // 85-99 skill range
        } else if (newCircuit.id === 'transcontinental') {
          newJockey.skill = Math.floor(Math.random() * 20) + 75; // 75-94 skill range
        }
        
        // Only add if meets minimum grade requirement
        const gradeValue = getGradeValue(RaceSimulator.getSkillGrade(newJockey.skill));
        const minGradeValue = getGradeValue(requirements.minGrade);
        
        if (gradeValue >= minGradeValue && !usedNames.has(newJockey.name)) {
          // Keep the weight from JockeyFactory.generateRandomJockey() (45-75kg range)
          selectedJockeys.push(newJockey);
          gameStore.jockeys.push(newJockey);
          usedNames.add(newJockey.name);
        }
      }

      availableJockeys.value = selectedJockeys;
      cachedJockeys.value.set(newCircuit.id, availableJockeys.value);
    }
  } else {
    finalizedOpponents.value = null;
    availableJockeys.value = [];
  }
}, { immediate: true });

const calculateTargetStatForGrade = (grade) => {
  const statValues = {
    'S+': 190,
    'S': 180,
    'S-': 170,
    'A+': 150,
    'A': 125,
    'A-': 110,
    'B+': 91,
    'B': 90,
    'B-': 80,
    'C+': 70,
    'C': 60,
    'C-': 50,
    'D+': 40,
    'D': 30,
    'D-': 20,
    'E': 10,
    'F': 5
  };

  return statValues[grade] || 50;
};

const preloadImages = () => {
  const colors = ['yellow', 'pink', 'black', 'gold', 'blue', 'green']
  const frames = [0, 1, 2]
  const images = []

  // Preload race track images
  images.push(new Image().src = require('@/assets/race_track.png'))
  images.push(new Image().src = require('@/assets/race_track_bg.png'))

  colors.forEach(color => {
    frames.forEach(frame => {
      const img = new Image()
      img.src = require(`@/assets/${color}_sprite_${frame}.png`)
      images.push(img)
    })
  })

  return images
}

onMounted(() => {
  preloadedImages.value = preloadImages()
  initializeDefaultCircuit()
})

onBeforeUnmount(() => {
  preloadedImages.value = []
})

const initializeDefaultCircuit = () => {
  const regional = availableCircuits.value.find(circuit => circuit.id === REGIONAL_CIRCUIT_ID)
  if (regional && canSelectCircuit(regional)) {
    selectedCircuit.value = regional
  }
}

const showCircuitRequirements = (circuit) => {
  const yourChocobo = props.selectedChocobo
  const requirements = []

  if (circuit.unlockLevel > yourChocobo.level) {
    requirements.push({
      text: `Level ${circuit.unlockLevel}+`,
      met: false
    })
  }

  const canAfford = gameStore.playerOwner.money >= circuit.entryFee
  requirements.push({
    text: `Entry Fee: ${circuit.entryFee}G`,
    met: canAfford
  })

  const eligibleRegions = circuit.getEligibleRegions(gameStore.playerOwner.homeRegion)
  const isRegionEligible = eligibleRegions.includes(gameStore.playerOwner.homeRegion)
  if (eligibleRegions.length === 1) {
    requirements.push({
      text: `Region: ${getRegionDisplayName(eligibleRegions[0])} only`,
      met: isRegionEligible
    })
  } else {
    const regionText = eligibleRegions.length === 6 
      ? 'Any region' 
      : `Eligible regions: ${eligibleRegions.map(r => getRegionDisplayName(r)).join(', ')}`
    requirements.push({
      text: regionText,
      met: isRegionEligible
    })
  }

  return requirements
}

const getRegionDisplayName = (region) => {
  const regions = {
    'region1': 'Eastern Metropolis',
    'region2': 'Junon Area',
    'region3': 'Western Seaboard',
    'region4': 'Central Range',
    'region5': 'Mideel Forest',
    'region6': 'Northern Territory',
    'region7': 'Stable Born'
  }
  return regions[region] || region
}

const canSelectCircuit = (circuit) => {
  const requirements = showCircuitRequirements(circuit)
  return requirements.every(req => req.met)
}

const potentialJockeyFee = computed(() => {
  if (!selectedJockey.value || !selectedCircuit.value) return null

  const jockeyGrade = RaceSimulator.getSkillGrade(selectedJockey.value.skill)
  const feeStructure = RaceSimulator.JOCKEY_FEES[jockeyGrade]
  
  return {
    winningFees: {
      first: Math.round(RACE_REWARDS[selectedRaceLength.value][1].money * selectedCircuit.value.rewardMultiplier * feeStructure.winningCut),
      second: Math.round(RACE_REWARDS[selectedRaceLength.value][2].money * selectedCircuit.value.rewardMultiplier * feeStructure.winningCut),
      third: Math.round(RACE_REWARDS[selectedRaceLength.value][3].money * selectedCircuit.value.rewardMultiplier * feeStructure.winningCut)
    },
    flatFee: feeStructure.flatFee,
    percentage: feeStructure.winningCut * 100
  }
})

const potentialPrizes = computed(() => {
  if (!selectedCircuit.value) return null

  const prizes = {
    first: RACE_REWARDS[selectedRaceLength.value][1].money * selectedCircuit.value.rewardMultiplier,
    second: RACE_REWARDS[selectedRaceLength.value][2].money * selectedCircuit.value.rewardMultiplier,
    third: RACE_REWARDS[selectedRaceLength.value][3].money * selectedCircuit.value.rewardMultiplier
  }

  if (potentialJockeyFee.value) {
    return {
      gross: prizes,
      net: {
        first: prizes.first - potentialJockeyFee.value.winningFees.first,
        second: prizes.second - potentialJockeyFee.value.winningFees.second,
        third: prizes.third - potentialJockeyFee.value.winningFees.third
      }
    }
  }

  return { gross: prizes, net: prizes }
})

const playerHighestLevel = computed(() => {
  return Math.max(...gameStore.playerChocobos.map(c => c.level))
})

const availableCircuits = computed(() => 
  getAvailableCircuits(playerHighestLevel.value, gameStore.playerOwner.homeRegion)
)

const canStartRace = computed(() => {
  if (!selectedJockey.value || !selectedCircuit.value) return false
  if (!finalizedOpponents.value || finalizedOpponents.value.length === 0) return false
  if (availableJockeys.value.length === 0) return false
  return gameStore.playerOwner.money >= selectedCircuit.value.entryFee
})

const showLoading = ref(false)

const startRace = async (withVisualization = false) => {
  if (!canStartRace.value || !selectedJockey.value) return
  
  showLoading.value = true
  
  // Force a tick and small delay to ensure loading state renders
  await nextTick()
  await new Promise(resolve => setTimeout(resolve, 50))

  try {
    // Deduct entry fee
    gameStore.playerOwner.money -= selectedCircuit.value.entryFee

    // Use finalized opponents 
    const selectedChocobos = [props.selectedChocobo, ...finalizedOpponents.value]
    racingChocobos.value = selectedChocobos

    const opponentJockeys = availableJockeys.value
      .filter(j => j.id !== selectedJockey.value.id)
      .slice(0, finalizedOpponents.value.length)

    const racingJockeys = [selectedJockey.value, ...opponentJockeys]

    const raceResult = await new Promise(resolve => {
      // Wrap race simulation in setTimeout to prevent UI blocking
      setTimeout(() => {
        const result = RaceSimulator.simulateRace(
          props.selectedChocobo,
          selectedJockey.value,
          racingChocobos.value,
          racingJockeys,
          selectedRaceLength.value,
          gameStore,
          selectedCircuit.value
        )
        resolve(result)
      }, 0)
    })

    const results = Array.isArray(raceResult) ? raceResult : raceResult.results

    const formattedResults = results.map(result => ({
      ...result,
      finalTime: RaceSimulator.formatRaceTime(result.finalScore)
    }))

    if (withVisualization) {
      showRaceVisualization.value = true
      raceResults.value = formattedResults
    } else {
      emit('race-complete', { 
        results: formattedResults,
        racingChocobos: racingChocobos.value,
        withVisualization: false,
        raceLength: selectedRaceLength.value,
        circuit: selectedCircuit.value
      })
    }
  } finally {
    showLoading.value = false
  }
}


const handleVisualizationComplete = () => {
  showRaceVisualization.value = false
  emit('race-complete', { 
    results: raceResults.value,
    racingChocobos: racingChocobos.value,
    withVisualization: true,
    raceLength: selectedRaceLength.value,
    circuit: selectedCircuit.value
  })
}

const getGenderIcon = (gender) => {
  return gender === 'male' ? '♂' : '♀'
}

const getGenderClass = (gender) => {
  return gender === 'male' ? 'text-blue-500' : 'text-pink-500'
}

const getOwnerName = (chocobo) => {
  const owner = gameStore.owners.find(owner => 
    owner.chocoboIds.includes(chocobo.id)
  )
  return owner ? owner.name : 'Unknown'
}

const onClose = () => {
  finalizedOpponents.value = null
  emit('close')
}

const getJockeyCompatibility = computed(() => {
  if (!selectedJockey.value || !selectedRaceLength.value) {
    return null;
  }

  const weights = {
    short: { speed: 0.7, stamina: 0.3 },
    medium: { speed: 0.5, stamina: 0.5 },
    long: { speed: 0.3, stamina: 0.7 }
  };

  const MIN_STAT = 65;
  const MAX_STAT = 200;
  const MIN_WEIGHT = 45;
  const MAX_WEIGHT = 75;
  const STAT_RANGE = MAX_STAT - MIN_STAT;
  const WEIGHT_RANGE = MAX_WEIGHT - MIN_WEIGHT;

  // Calculate optimal weight based on race type and chocobo stats
  const combinedStats = (props.selectedChocobo.stamina * weights[selectedRaceLength.value].stamina) + 
                       (props.selectedChocobo.speed * weights[selectedRaceLength.value].speed);
  const normalizedStats = Math.max(MIN_STAT, Math.min(MAX_STAT, combinedStats));
  const optimalWeight = MIN_WEIGHT + ((normalizedStats - MIN_STAT) / STAT_RANGE) * WEIGHT_RANGE;
  
  // Calculate weight difference and score
  const weightDiff = Math.abs(selectedJockey.value.weight - optimalWeight);
  const maxPenalty = selectedRaceLength.value === 'short' ? 0.15 : 
                    selectedRaceLength.value === 'medium' ? 0.12 : 0.08;
  
  const weightScore = Math.max(0, 100 - ((weightDiff / (WEIGHT_RANGE / 2)) * 100));
  
  // Calculate consistency based on skill
  const consistencyScore = Math.min(100, (selectedJockey.value.skill / 100) * 100);
  
  // Get recommendation
  let recommendation = '';
  if (weightScore >= 80 && consistencyScore >= 80) {
    recommendation = '"They\'re made for one another"';
  } else if (weightScore >= 70 && consistencyScore >= 60) {
    recommendation = '"This is a Good Match."';
  } else if (weightScore >= 60) {
    recommendation = '"Decent weight, I\'d look for a higher class though."';
  } else if (consistencyScore >= 60) {
    recommendation = '"They\'ve got good skill, we want a different weight class ideally."';
  } else {
    recommendation = '"I\'d not go with this Jock, boss."';
  }
  let topOptimalWeight = optimalWeight + 5
  let lowOptimalWeight = optimalWeight.toFixed(0) - 5
  let optimalWeightString = 'Between '
  optimalWeightString = optimalWeightString.concat(lowOptimalWeight, ' and ', topOptimalWeight.toFixed(0))
  return {
    optimalWeight: optimalWeightString,
    currentWeight: selectedJockey.value.weight,
    weightDifference: weightDiff,
    compatibilityScore: weightScore,
    consistencyScore: consistencyScore,
    maxPenalty: maxPenalty,
    recommendation: recommendation
  };
});

</script>

<template>
  <div :class="`fixed inset-0 ${applyThemeClasses('components.modal.overlay')} flex items-center justify-center z-50 overflow-y-auto`">
    <div :class="`rounded-lg ${applyThemeClasses('components.modal.container')} w-full min-w-[350px] max-w-4xl mx-auto my-2`">
      <!-- Header -->
      <div class="flex justify-between items-center p-3 border-b">
        <h2 :class="applyThemeClasses('text.primary')">Race Setup</h2>
        <button 
          @click="onClose"
          class="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      <!-- Scrollable Content -->
      <div class="max-h-[calc(100vh-8rem)] overflow-y-auto">
        <!-- Circuit Selection -->
        <div class="p-3 border-b">
          <h3 :class="applyThemeClasses('text.primaryLow')">Select Racing Circuit</h3>
          <div class="grid grid-cols-1 gap-2">
            <div
              v-for="circuit in availableCircuits"
              :key="circuit.id"
              @click="canSelectCircuit(circuit) ? selectedCircuit = circuit : null"
              :class="[
                'p-2 border rounded-lg transition-colors',
                !canSelectCircuit(circuit) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                selectedCircuit?.id === circuit.id
                  ? 'border-blue-500 bg-blue-500 bg-opacity-10'
                  : 'hover:bg-gray-50'
              ]"
            >
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0 flex-1">
                  <h4 :class="applyThemeClasses('text.secondaryBold')">{{ circuit.name }}</h4>
                  <p :class="`text-xs ${applyThemeClasses('text.tertiary')}`">{{ circuit.description }}</p>
                  
                  <!-- Requirements List -->
                  <div class="mt-2 space-y-1">
                    <div 
                      v-for="(req, idx) in showCircuitRequirements(circuit)"
                      :key="idx"
                      :class="[
                        'text-xs flex items-center gap-1',
                        req.met ? 'text-green-600' : 'text-red-500'
                      ]"
                    >
                      <span v-if="req.met" class="text-green-500">✓</span>
                      <span v-else class="text-red-500">✗</span>
                      {{ req.text }}
                    </div>
                  </div>
                </div>
                
                <div class="text-right shrink-0">
                  <span class="text-green-500 text-sm">{{ circuit.entryFee }}G</span>
                  <div class="text-xs mt-1">
                    <span class="text-blue-500">×{{ circuit.xpMultiplier }}XP</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Race Length Selection -->
        <div class="p-3 border-b">
          <h3 :class="`${applyThemeClasses('text.primaryLow')}`">Race Length</h3>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="race in RACE_LENGTHS"
              :key="race.id"
              @click="selectedRaceLength = race.id"
              :class="[
                'p-2 border rounded-lg cursor-pointer flex-1 min-w-[150px]',
                selectedRaceLength === race.id
                  ? 'border-blue-500 bg-blue-500 bg-opacity-10'
                  : 'hover:bg-gray-50'
              ]"
            >
              <h4 :class="`${applyThemeClasses('text.secondaryBold')}`">{{ race.name }}</h4>
              <p :class="`text-xs ${applyThemeClasses('text.secondary')}`">{{ race.description }}</p>
            </div>
          </div>
        </div>
        <!-- Race Participants -->
        <div class="p-3 border-b">
          <h3 :class="`${applyThemeClasses('text.primaryLow')}`">Participants</h3>
          
          <!-- Selected Chocobo Card -->
          <div class="mb-3">
            <div class="border rounded-lg overflow-hidden">
              <div :class="[getChocoboColorClass(selectedChocobo.color), 'p-2']">
                <div class="flex justify-between items-start gap-2">
                  <div class="min-w-0">
                    <h4 class="font-bold text-white text-sm truncate">{{ selectedChocobo.name }}</h4>
                    <p class="text-xs text-white opacity-90">Your Chocobo</p>
                  </div>
                  <span :class="[getGenderClass(selectedChocobo.gender), 'text-lg font-bold shrink-0']">
                    {{ getGenderIcon(selectedChocobo.gender) }}
                  </span>
                </div>
              </div>
              <div class="p-2">
                <div class="flex flex-wrap gap-1">
                  <span :class="[getStatGradeClass(getStatGrade(selectedChocobo.speed)), 'px-1.5 py-0.5 rounded text-xs font-medium']">
                    Speed: {{ getStatGrade(selectedChocobo.speed) }}
                  </span>
                  <span :class="[getStatGradeClass(getStatGrade(selectedChocobo.stamina)), 'px-1.5 py-0.5 rounded text-xs font-medium']">
                    Stamina: {{ getStatGrade(selectedChocobo.stamina) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Opponents List -->
          <div class="space-y-2">
            <div 
              v-for="chocobo in finalizedOpponents" 
              :key="chocobo.id"
              class="border rounded-lg overflow-hidden"
            >
              <div :class="[getChocoboColorClass(chocobo.color), 'p-2']">
                <div class="flex justify-between items-start gap-2">
                  <div class="min-w-0">
                    <h4 class="font-bold text-white text-sm truncate">{{ chocobo.name }}</h4>
                    <p class="text-xs text-white opacity-90 truncate">{{ getOwnerName(chocobo) }}</p>
                  </div>
                  <span :class="[getGenderClass(chocobo.gender), 'text-lg font-bold shrink-0']">
                    {{ getGenderIcon(chocobo.gender) }}
                  </span>
                </div>
              </div>
              <div class="p-2">
                <div class="flex flex-wrap gap-1">
                  <span :class="[getStatGradeClass(getStatGrade(chocobo.speed)), 'px-1.5 py-0.5 rounded text-xs font-medium']">
                    Speed: {{ getStatGrade(chocobo.speed) }}
                  </span>
                  <span :class="[getStatGradeClass(getStatGrade(chocobo.stamina)), 'px-1.5 py-0.5 rounded text-xs font-medium']">
                    Stamina: {{ getStatGrade(chocobo.stamina) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Jockey Selection -->
        <div class="p-3 border-b">
          <h3 :class="`${applyThemeClasses('text.primaryLow')}`">Select Jockey</h3>
          <div class="space-y-2">
            <div 
              v-for="jockey in availableJockeys" 
              :key="jockey.id"
              @click="selectedJockey = jockey"
              :class="[
                'p-2 border rounded-lg cursor-pointer',
                selectedJockey?.id === jockey.id 
                  ? 'border-blue-500 bg-blue-500 bg-opacity-10' 
                  : 'hover:bg-gray-50'
              ]"
            >
              <div class="flex items-start gap-2">
                <div class="min-w-0 flex-1">
                  <h4 :class="`${applyThemeClasses('text.secondaryBold')} truncate`">{{ jockey.name }}</h4>
                  <p :class="`text-xs ${applyThemeClasses('text.secondary')}`">{{ jockey.weightCategory }}</p>
                  <div class="flex items-center gap-1 mt-1">
                    <span :class="[
                      'px-1.5 py-0.5 rounded text-xs font-medium',
                      getStatGradeClass(jockey.skillGrade)
                    ]">
                      {{ jockey.skillGrade }}
                    </span>
                  </div>
                </div>
                <div class="text-xs shrink-0">
                  <p :class="`text-xs ${applyThemeClasses('text.secondaryBold')}`">Form:</p>
                  <div class="flex gap-0.5 mt-1">
                    <span 
                      v-for="(result, index) in jockey.recentForm"
                      :key="index"
                      :class="[
                        'w-4 h-4 flex items-center justify-center rounded text-xs',
                        result === null 
                          ? 'bg-gray-100' 
                          : result === 1 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : result === 2 
                              ? 'bg-gray-100 text-gray-800'
                              : result === 3
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-red-50 text-red-800'
                      ]"
                    >
                      {{ result === null ? '-' : result }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Compatibility Info (shows when selected) -->
              <div 
                v-if="selectedJockey?.id === jockey.id && getJockeyCompatibility"
                class="mt-4 pt-4 border-t"
              >
                <div class="space-y-2">
                  <!-- Optimal Weight -->
                  <div class="flex justify-between text-sm">
                    <span :class="`${applyThemeClasses('text.secondaryBold')}`">Desired weight:</span>
                    <span :class="`${applyThemeClasses('text.secondary')}`">
                      {{ getJockeyCompatibility.optimalWeight }}kg
                    </span>
                    <span :class="`${applyThemeClasses('text.secondaryBold')}`">Jockey weight:</span>
                      <span :class="`${applyThemeClasses('text.secondary')}`">
                        {{ getJockeyCompatibility.currentWeight }}kg
                      </span>
                  </div>

                  <!-- Weight Compatibility -->
                  <div class="flex items-center gap-2">
                    <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        class="h-full transition-all duration-300"
                        :class="[
                          getJockeyCompatibility.compatibilityScore >= 80 ? 'bg-green-500' :
                          getJockeyCompatibility.compatibilityScore >= 60 ? 'bg-yellow-500' :
                          'bg-red-500'
                        ]"
                        :style="{ width: `${getJockeyCompatibility.compatibilityScore}%` }"
                      ></div>
                    </div>
                  </div>

                  <!-- Recommendation -->
                  <div 
                    class="text-sm font-medium mt-2"
                    :class="[
                      getJockeyCompatibility.compatibilityScore >= 80 ? 'text-green-600' :
                      getJockeyCompatibility.compatibilityScore >= 60 ? 'text-yellow-600' :
                      'text-red-500'
                    ]"
                  >
                    {{ getJockeyCompatibility.recommendation }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Prize Information -->
        <div v-if="potentialPrizes" class="p-3 border-b">
          <h3 :class="`text-xs ${applyThemeClasses('text.secondaryBold')}`">Potential Prizes</h3>
          <div class="grid grid-cols-3 gap-4">
            <div class="text-center">
              <div class="text-sm font-medium text-yellow-700">1st Place</div>
              <div :class="`text-xs ${applyThemeClasses('text.money')}`">{{ potentialPrizes.gross.first }}G</div>
              <div v-if="potentialJockeyFee" :class="`text-xs ${applyThemeClasses('text.secondary')}`">
                Net: {{ potentialPrizes.net.first }}G
              </div>
            </div>
            <div class="text-center">
              <div class="text-sm font-medium text-gray-500">2nd Place</div>
              <div :class="`text-xs ${applyThemeClasses('text.money')}`">{{ potentialPrizes.gross.second }}G</div>
              <div v-if="potentialJockeyFee" :class="`text-xs ${applyThemeClasses('text.secondary')}`">
                Net: {{ potentialPrizes.net.second }}G
              </div>
            </div>
            <div class="text-center">
              <div class="text-sm font-medium text-amber-600">3rd Place</div>
              <div :class="`text-xs ${applyThemeClasses('text.money')}`">{{ potentialPrizes.gross.third }}G</div>
              <div v-if="potentialJockeyFee" :class="`text-xs ${applyThemeClasses('text.secondary')}`">
                Net: {{ potentialPrizes.net.third }}G
              </div>
            </div>
          </div>
          
          <div v-if="potentialJockeyFee" :class="`mt-2 text-xs ${applyThemeClasses('text.secondary')} text-center`">
            Jockey takes {{ potentialJockeyFee.percentage }}% of winnings
            <br>
            Base fee: {{ potentialJockeyFee.flatFee }}G (if no prize money)
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="p-3 border-t">
        <div class="flex justify-end gap-2">
          <button 
            @click="onClose"
            :class="`rounded-lg ${applyThemeClasses('form.button.outline')}`"
          >
            <p :class="`rounded-lg ${applyThemeClasses('text.secondary')} p-2`">Cancel</p>
          </button>
          <div class="relative" v-if="canStartRace">
            <button 
              @click="() => startRace(false)"
              class="px-4 py-2 rounded-lg text-white bg-green-500 hover:bg-green-600"
            >
              Quick Race
            </button>
            <button 
              @click="() => startRace(true)"
              class="px-4 py-2 ml-2 rounded-lg text-white bg-blue-500 hover:bg-blue-600"
            >
              Watch Race
            </button>
          </div>
          <button 
            v-else
            disabled
            class="px-4 py-2 rounded-lg text-white bg-gray-400 cursor-not-allowed"
          >
            Start Race ({{ selectedCircuit?.entryFee || 0 }}G)
          </button>
        </div>
      </div>
      <RaceVisualization
        v-if="showRaceVisualization"
        :results="raceResults"
        :racing-chocobos="racingChocobos" 
        :race-length="selectedRaceLength"
        :circuit="selectedCircuit"
        @complete="handleVisualizationComplete"
      />
      <RaceLoadingOverlay v-if="showLoading" />
    </div>
  </div>
</template>